/**
 * WebGPU capability report. Logged at startup and shown in the debug panel so
 * browser/GPU differences are visible in bug reports.
 */
export interface Capabilities {
  adapter: string;
  features: string[];
  timestampQuery: boolean;
  float32Filterable: boolean;
  shaderF16: boolean;
  limits: Record<string, number>;
}

const INTERESTING_LIMITS = [
  'maxTextureDimension2D',
  'maxTextureDimension3D',
  'maxTextureArrayLayers',
  'maxStorageBufferBindingSize',
  'maxBufferSize',
  'maxComputeWorkgroupsPerDimension',
  'maxComputeInvocationsPerWorkgroup',
  'maxStorageTexturesPerShaderStage',
  'maxSampledTexturesPerShaderStage',
  'maxColorAttachments',
] as const;

export async function detectCapabilities(): Promise<Capabilities | null> {
  if (typeof navigator === 'undefined' || !('gpu' in navigator) || !navigator.gpu) return null;
  let adapter: GPUAdapter | null = null;
  try {
    adapter = await navigator.gpu.requestAdapter({ powerPreference: 'high-performance' });
  } catch {
    return null;
  }
  if (!adapter) return null;
  const info = adapter.info;
  const features = [...adapter.features.values()].map(String).sort();
  const limits: Record<string, number> = {};
  for (const k of INTERESTING_LIMITS) {
    const v = (adapter.limits as unknown as Record<string, number>)[k];
    if (typeof v === 'number') limits[k] = v;
  }
  return {
    adapter: [info?.vendor, info?.architecture, info?.description].filter(Boolean).join(' / ') || 'unknown',
    features,
    timestampQuery: adapter.features.has('timestamp-query'),
    float32Filterable: adapter.features.has('float32-filterable'),
    shaderF16: adapter.features.has('shader-f16'),
    limits,
  };
}

/** Limits we want raised above WebGPU defaults when the adapter allows it. */
export function requestedLimits(caps: Capabilities): Record<string, number> {
  const want: Record<string, number> = {
    maxStorageBufferBindingSize: 512 * 1024 * 1024,
    maxBufferSize: 512 * 1024 * 1024,
    maxTextureDimension2D: 8192,
    maxStorageTexturesPerShaderStage: 8,
    maxColorAttachments: 8,
  };
  const out: Record<string, number> = {};
  for (const [k, v] of Object.entries(want)) {
    const max = caps.limits[k];
    if (typeof max === 'number') out[k] = Math.min(v, max);
  }
  return out;
}
